import React, { useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import CustomTextFieldDynamic from "../../../components/Input/CustomTextFieldDynamic";
import CommentHeader from "./CommentHeader";
import { fadeIn } from "../../../styles/springs";

interface CommentProps {
  date: Date;
  defaultFocused?: boolean;
  defaultValue: string;
  editPermission: boolean;
  handleBlur?: () => void;
  handleCancel: () => void;
  handleCreate?: () => Promise<void>;
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDelete?: (commentId: number) => void;
  handleDirtyChange?: (resetFlag?: boolean) => void;
  handleSave?: (commentId: number, payload: string) => Promise<void>;
  hide?: boolean;
  id?: number;
  includeUpdateNotification?: boolean;
  keepFocusAfterEdit?: boolean;
  saveOnBlur?: boolean;
  time: string;
  username: string;
  value?: string;
}

const Comment = (props: CommentProps) => {
  const {
    date,
    defaultFocused,
    defaultValue,
    editPermission,
    handleBlur,
    handleCancel,
    handleChange,
    handleCreate,
    handleDelete,
    handleDirtyChange,
    handleSave,
    hide,
    id,
    includeUpdateNotification,
    keepFocusAfterEdit,
    username,
    saveOnBlur,
    time,
    value,
  } = props;
  const [inputValue, setInputValue] = useState(defaultValue);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  const springProps = useSpring(fadeIn);

  const handleUpdateNotification = () => {
    if (includeUpdateNotification) {
      setShowUpdateNotification(true);
      setTimeout(() => {
        setShowUpdateNotification(false);
      }, 3000);
    }
  };

  const handleLocalSave = () => {
    if (handleSave) {
      handleSave(id as number, inputValue).then(() => {
        handleUpdateNotification();
      });
    } else if (handleCreate) {
      handleCreate().then(() => {
        setInputValue("");
        handleUpdateNotification();
      });
    }
  };

  useEffect(() => {
    if (handleDirtyChange && inputValue === defaultValue) {
      handleDirtyChange(true);
    }
  }, [inputValue]);

  return (
    <animated.div style={springProps}>
      <CustomTextFieldDynamic
        defaultFocused={defaultFocused}
        header={
          <CommentHeader date={date} updateNotification={showUpdateNotification} username={username} time={time} />
        }
        hide={hide}
        dirtyFlag={inputValue !== defaultValue}
        editPermission={editPermission}
        keepFocusAfterEdit={keepFocusAfterEdit}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (handleChange) {
            handleChange(e);
          }
          if (handleDirtyChange) {
            handleDirtyChange();
          }
          setInputValue(e.target.value);
        }}
        onDelete={handleDelete ? () => handleDelete(id as number) : undefined}
        onCancel={() => {
          setInputValue(defaultValue);
          handleCancel();
        }}
        onSave={handleLocalSave}
        onBlur={handleBlur}
        saveOnBlur={saveOnBlur}
        value={value || inputValue}
      />
    </animated.div>
  );
};

export default Comment;
